import { latToPx, lngToPx, metresPerPxAtZoom0, TILE_SIZE } from '@/core/utils';
import { loadElevationTile } from './tileCache';

export interface BBox {
  n: number;
  s: number;
  e: number;
  w: number;
}

/** Ground elevation in metres at a point, bilinearly interpolated. */
export type ElevationSampler = (lat: number, lng: number) => number;

/** Tile zoom whose pixels are a bit finer than the grid cell, within what the DEM offers. */
function demZoomFor(lat: number, cellSize: number) {
  return Math.min(14, Math.max(8, Math.ceil(Math.log2(metresPerPxAtZoom0(lat) / (cellSize * 0.6)))));
}

/** Loads all DEM tiles covering `bbox` and returns a sampler over them. */
export async function loadElevationSampler(bbox: BBox, cellSize: number): Promise<ElevationSampler> {
  const z = demZoomFor((bbox.n + bbox.s) / 2, cellSize);
  const x0 = Math.floor(lngToPx(bbox.w, z) / TILE_SIZE);
  const x1 = Math.floor(lngToPx(bbox.e, z) / TILE_SIZE);
  const y0 = Math.floor(latToPx(bbox.n, z) / TILE_SIZE);
  const y1 = Math.floor(latToPx(bbox.s, z) / TILE_SIZE);

  const tiles = new Map<string, Float32Array>();
  const jobs: Promise<unknown>[] = [];
  for (let x = x0; x <= x1; x++) {
    for (let y = y0; y <= y1; y++) {
      jobs.push(loadElevationTile(z, x, y).then((h) => tiles.set(`${x}/${y}`, h)));
    }
  }
  await Promise.all(jobs);

  // Pixel lookup, clamped to the loaded tiles.
  const at = (px: number, py: number) => {
    const tx = Math.floor(px / TILE_SIZE);
    const ty = Math.floor(py / TILE_SIZE);
    const tile = tiles.get(`${Math.min(x1, Math.max(x0, tx))}/${Math.min(y1, Math.max(y0, ty))}`)!;
    const ix = Math.min(TILE_SIZE - 1, Math.max(0, px - tx * TILE_SIZE));
    const iy = Math.min(TILE_SIZE - 1, Math.max(0, py - ty * TILE_SIZE));
    return tile[iy * TILE_SIZE + ix];
  };

  return (lat, lng) => {
    const px = lngToPx(lng, z) - 0.5;
    const py = latToPx(lat, z) - 0.5;
    const fx = Math.floor(px);
    const fy = Math.floor(py);
    const dx = px - fx;
    const dy = py - fy;
    return (
      at(fx, fy) * (1 - dx) * (1 - dy) +
      at(fx + 1, fy) * dx * (1 - dy) +
      at(fx, fy + 1) * (1 - dx) * dy +
      at(fx + 1, fy + 1) * dx * dy
    );
  };
}
